import React, { useState, useEffect } from "react";
import { FiX } from "react-icons/fi";
import { useBodyScrollLock } from "../../../hooks/useBodyScrollLock";
import axiosInstance from "../../../utils/axios";
const MemberCard = ({ member, index, handleImageClick }) => {
  return (
    <div
      key={index}
      className="relative group rounded-2xl flex flex-col items-center text-center transition-all duration-500 ease-out overflow-hidden"
      style={{
        willChange: "transform",
        backfaceVisibility: "hidden",
      }}
    >
      {/* Card content container */}
      <div className="relative w-full h-[16rem] sm:h-[17rem] bg-white rounded-2xl shadow-md group-hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2 active:scale-95 border border-gray-200 group-hover:border-[#8cc441]/40 pt-6">
        {/* Profile image container */}
        <div className="relative flex justify-center mb-4">
          <div
            className="relative w-24 h-24  sm:w-32 sm:h-32 rounded-full cursor-pointer group/img"
            onClick={() => handleImageClick(member.profile, member.name)}
          >
            {/* Outer glow ring */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-[#8cc441] to-[#a6d65f] opacity-0 group-hover:opacity-100 transition-all duration-500 blur-md scale-110"></div>

            {/* Border */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-[#8cc441] via-[#a6d65f] to-[#8cc441] p-1">
              <div className="w-full h-full rounded-full bg-white p-1">
                <img
                  src={member.profile}
                  alt={member.name}
                  className="w-full h-full object-cover rounded-full transition-all duration-500 group-hover/img:scale-105 group-hover/img:brightness-110"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content section */}
        <div className="px-4 pb-6 relative z-10">
          {/* Name with underline animation */}
          <div className="relative inline-block w-full mb-2">
            <h3 className="font-bold text-sm lg:text-lg text-[#02323F] transition-all duration-300 group-hover:text-[#004680]">
              {member.name}
            </h3>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 group-hover:w-16 h-0.5 bg-gradient-to-r from-transparent via-[#8cc441] to-transparent transition-all duration-500"></div>
          </div>

          {/* Designation with dots */}
          <div className="flex items-center justify-center gap-2 text-sm text-[#6b7280] transition-all duration-300 group-hover:text-[#1f2937]">
            <div className="w-1 h-1 rounded-full bg-[#8cc441] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <p className="text-xs sm:text-base font-medium">
              {member.designation}
            </p>
            <div className="w-1 h-1 rounded-full bg-[#8cc441] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>

          {/* Decorative bottom bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#8cc441]/50 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-b-2xl"></div>
        </div>
      </div>
    </div>
  );
};
const OurTeamBodyContainer = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedName, setSelectedName] = useState("");
  const [imageLoaded, setImageLoaded] = useState(false);

  useBodyScrollLock(selectedImage);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await axiosInstance.get("/teams");
        setTeamMembers(res.data.team || []);
      } catch (err) {
        console.error("Failed to load team:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, []);

  const handleImageClick = (image, name) => {
    setSelectedImage(image);
    setSelectedName(name);
    setImageLoaded(false);
  };

  const closeModal = () => {
    setSelectedImage(null);
    setSelectedName("");
    setImageLoaded(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") closeModal();
  };

  useEffect(() => {
    if (selectedImage) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage]);

  return (
    <div className="px-4 sm:px-6 md:px-10  overflow-hidden">
      <div className="sm:py-8">
        {/* Loading skeleton */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-10 animate-pulse">
            {[...Array(6)].map((_, idx) => (
              <div
                key={idx}
                className="bg-gray-200 h-[14rem] rounded-xl shadow"
              ></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 my-4 lg:grid-cols-3 gap-2 sm:gap-4 lg:gap-8">
            {teamMembers.map((member, index) => (
              <MemberCard
                key={index}
                member={member}
                index={index}
                handleImageClick={handleImageClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeModal}
          aria-modal="true"
          role="dialog"
        >
          <button
            className="absolute top-4 right-4 z-10 text-white text-2xl hover:text-gray-300 transition-colors duration-200 bg-black/30 backdrop-blur-sm rounded-full p-3 hover:bg-black/50"
            onClick={closeModal}
          >
            <FiX />
          </button>

          <div className="absolute top-4 left-4 text-white/70 text-sm bg-black/30 backdrop-blur-sm px-3 py-2 rounded-lg">
            Press ESC to close
          </div>

          <div
            className="max-w-6xl w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
              </div>
            )}

            <img
              src={selectedImage}
              alt={selectedName}
              className={`max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
              onLoad={() => setImageLoaded(true)}
              style={{
                maxHeight: "calc(100vh - 120px)",
                maxWidth: "calc(100vw - 32px)",
              }}
            />

            {selectedName && imageLoaded && (
              <div className="mt-6 text-center">
                <p className="text-white text-xl md:text-2xl font-semibold mb-2">
                  {selectedName}
                </p>
                <div className="w-24 h-1 bg-white/30 mx-auto rounded-full"></div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OurTeamBodyContainer;

// DONT DELETED THE CODE BELOW

// import React, { useState } from "react";
// import GeneralManagerImg from "../../../Assets/Images/our-team/Rohit Biswal.png";
// import StateHeadProjetcsImg from "../../../Assets/Images/our-team/Premananda Biswal.png";
// import DrAlexanderDas from "../../../Assets/Images/our-team/DrAlexanderDas.png";
// import Saswati from "../../../Assets/Images/our-team/Saswati Biswal.png";
// import Vijay from "../../../Assets/Images/our-team/Vijay Nayak.png";
// import Tapan_das from "../../../Assets/Images/our-team/Tapan_das.jpg";
// import { FiX } from "react-icons/fi";
// import { useBodyScrollLock } from "../../../hooks/useBodyScrollLock";
// // import OurFamilyImg from "../../../Assets/Images/our-team/Our Extended Family.png";

// const teamMembers = [
//   {
//     name: "Er. Alexzandar Das",
//     role: "Founder, Director & CEO",
//     image: DrAlexanderDas,
//   },
//   {
//     name: "Mr. Bijaya Prasad Nayak",
//     role: "Head Administration",
//     image: Vijay,
//   },
//   {
//     name: "Premananda Biswal",
//     role: "State Head Projects",
//     image: StateHeadProjetcsImg,
//   },
//   {
//     name: "Tapan Kumar Das",
//     role: "Director,PR& Liaison",
//     image: Tapan_das,
//   },
//   {
//     name: "Saswati Priyadarsini Biswal",
//     role: "Head HR",
//     image: Saswati,
//   },
//   {
//     name: "Mr. Rohit Biswal",
//     role: "General Manager",
//     image: GeneralManagerImg,
//   },
// ];

// const OurTeamBodyContainer = () => {
//   const [selectedImage, setSelectedImage] = useState(null);
//   const [selectedName, setSelectedName] = useState("");
//   const [imageLoaded, setImageLoaded] = useState(false);

//   useBodyScrollLock(selectedImage);

//   const handleImageClick = (image, name) => {
//     setSelectedImage(image);
//     setSelectedName(name);
//     setImageLoaded(false);
//   };

//   const closeModal = () => {
//     setSelectedImage(null);
//     setSelectedName("");
//     setImageLoaded(false);
//   };

//   const handleKeyDown = (event) => {
//     if (event.key === "Escape") {
//       closeModal();
//     }
//   };

//   React.useEffect(() => {
//     if (selectedImage) {
//       document.addEventListener("keydown", handleKeyDown);
//       return () => document.removeEventListener("keydown", handleKeyDown);
//     }
//   }, [selectedImage]);

//   return (
//     <div className="px-4 sm:px-6 md:px-10 pb-16 overflow-hidden">
//       <div className="pt-16 md:pt-18">
//         <div className="grid grid-cols-2 mt-4 lg:grid-cols-3 gap-y-20 sm:gap-y-16 lg:gap-y-20 gap-x-8">
//           {teamMembers.map((member, index) => (
//             <div
//               key={index}
//               data-aos="fade-up"
//               data-aos-delay={index * 120}
//               className={`relative group rounded-xl flex flex-col items-center text-center transition-all duration-500 ease-out
//                 ${
//                   member.isFamily
//                     ? "p-0 bg-transparent shadow-none"
//                     : "bg-[#7FCEE226]/15 shadow-md h-[14rem] hover:shadow-xl hover:-translate-y-2 active:scale-95"
//                 }`}
//               style={{
//                 // Ensure hover effects don't cause overflow
//                 willChange: "transform",
//                 backfaceVisibility: "hidden",
//               }}
//             >
//               {member.isFamily ? (
//                 <div
//                   className="relative w-full h-full cursor-pointer group"
//                   onClick={() => handleImageClick(member.image, member.name)}
//                 >
//                   <div className="relative overflow-hidden rounded-lg shadow-md transition-transform duration-300 group-hover:scale-[1.02]">
//                     <img
//                       src={member.image}
//                       alt={member.name}
//                       className="w-full h-full object-cover"
//                       loading="lazy"
//                     />
//                     {/* Overlay for better text visibility */}
//                     <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
//                   </div>

//                   <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white text-sm md:text-base px-4 py-2 rounded-lg shadow-lg transition-all duration-300 group-hover:bg-black/80">
//                     {member.name}
//                   </div>
//                 </div>
//               ) : (
//                 <>
//                   <div className="relative">
//                     <div
//                       className="w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden shadow-md border-4 border-white -mt-14 md:-mt-16 mb-4 transition-all duration-500 group-hover:scale-105 cursor-pointer relative z-10"
//                       onClick={() =>
//                         handleImageClick(member.image, member.name)
//                       }
//                     >
//                       <img
//                         src={member.image}
//                         alt={member.name}
//                         className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
//                         loading="lazy"
//                       />
//                     </div>
//                   </div>

//                   <div className="px-2">
//                     <h3 className="font-semibold text-lg text-[#08121A] mb-1 transition-all duration-500 group-hover:opacity-90 group-hover:text-[#06171F]">
//                       {member.name}
//                     </h3>
//                     <p className="text-sm text-[#4D4D4D] transition-all duration-500 group-hover:-translate-y-1 group-hover:text-[#333333]">
//                       {member.role}
//                     </p>
//                   </div>
//                 </>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>

//       {/* Enhanced Fullscreen Modal */}
//       {selectedImage && (
//         <div
//           className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
//           onClick={closeModal}
//           role="dialog"
//           aria-modal="true"
//           aria-labelledby="modal-title"
//         >
//           {/* Close button */}
//           <button
//             className="absolute top-4 right-4 z-10 text-white text-2xl hover:text-gray-300 transition-colors duration-200 bg-black/30 backdrop-blur-sm rounded-full p-3 hover:bg-black/50"
//             onClick={closeModal}
//             aria-label="Close image preview"
//           >
//             <FiX />
//           </button>

//           {/* ESC hint */}
//           <div className="absolute top-4 left-4 text-white/70 text-sm bg-black/30 backdrop-blur-sm px-3 py-2 rounded-lg">
//             Press ESC to close
//           </div>

//           {/* Modal content */}
//           <div
//             className="max-w-6xl w-full flex flex-col items-center"
//             onClick={(e) => e.stopPropagation()}
//           >
//             {/* Loading state */}
//             {!imageLoaded && (
//               <div className="absolute inset-0 flex items-center justify-center">
//                 <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
//               </div>
//             )}

//             <img
//               src={selectedImage}
//               alt={selectedName}
//               className={`max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${
//                 imageLoaded ? "opacity-100" : "opacity-0"
//               }`}
//               onLoad={() => setImageLoaded(true)}
//               style={{
//                 maxHeight: "calc(100vh - 120px)",
//                 maxWidth: "calc(100vw - 32px)",
//               }}
//             />

//             {/* Image title */}
//             {selectedName && imageLoaded && (
//               <div className="mt-6 text-center">
//                 <p className="text-white text-xl md:text-2xl font-semibold mb-2">
//                   {selectedName}
//                 </p>
//                 <div className="w-24 h-1 bg-white/30 mx-auto rounded-full"></div>
//               </div>
//             )}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default OurTeamBodyContainer;
